import { useEffect, useRef } from 'react'

interface AdUnitProps {
    /** AdSense ad-unit slot id, from the AdSense dashboard. */
    slot: string
    /** Responsive by default; pass a fixed format if the slot needs one. */
    format?: string
}

/**
 * One AdSense display unit.
 *
 * The previous UI left its `<ins class="adsbygoogle">` commented out, so the
 * loader ran but nothing ever rendered or earned. This renders the unit and
 * pushes it, once, so it actually fills.
 *
 * Renders nothing when VITE_ADSENSE is unset, so a build without an AdSense id
 * -- or an ad blocker -- leaves no empty box. The reserved slot around it
 * collapses when this is absent (see the route's :empty rule).
 */
export default function AdUnit({ slot, format = 'auto' }: AdUnitProps) {
    const client = import.meta.env.VITE_ADSENSE
    const pushed = useRef(false)

    useEffect(() => {
        if (!client || pushed.current) return
        try {
            (window.adsbygoogle = window.adsbygoogle || []).push({})
            pushed.current = true
        } catch {
            // the loader has not arrived yet or was blocked; nothing to render
        }
    }, [client])

    if (!client) return null

    return (
        <ins
            className="adsbygoogle"
            style={{ display: 'block' }}
            data-ad-client={client}
            data-ad-slot={slot}
            data-ad-format={format}
            data-full-width-responsive="true"
        />
    )
}
